// Countries whose carriers price by sub-zone rather than by country alone.
// The rate platform keys each one differently, so the form has to ask for the
// right thing - a US state code is not a postcode - and label it accordingly.
//
// Mirrors POSTCODE_ZONED_COUNTRIES in
// backend/services/priority_connections_service.py. Change both together.
//
// Leading zeros matter (NZ 0110, MY 01000): these values are strings from the
// input to the API. Never parse them as numbers.

// The platform wants a 2-letter state code, and the list lives in one place -
// src/data/usStates.json, mirroring backend/data/us_states.json - so the picker
// and the value the backend will accept can never drift apart.
import US_STATE_MAP from '../data/usStates.json';

export const US_STATES = Object.entries(US_STATE_MAP.states)
  .map(([code, name]) => [code, name])
  .sort((a, b) => a[1].localeCompare(b[1]));

const ZONES = {
  AU: { label: 'Destination postcode', placeholder: '3000', maxLength: 4, mode: 'numeric', hint: '4-digit Australian postcode' },
  NZ: { label: 'Destination postcode', placeholder: '0110', maxLength: 4, mode: 'numeric', hint: '4-digit New Zealand postcode' },
  MY: { label: 'Destination postcode', placeholder: '01000', maxLength: 5, mode: 'numeric', hint: '5-digit Malaysian postcode' },
  // Canada zones on the FSA - the first three characters of the postal code -
  // not the province, so a province picker would be the wrong question.
  CA: { label: 'Destination postal code', placeholder: 'V1M 2X3', maxLength: 7, mode: 'text', hint: 'First 3 characters are enough' },
  US: { label: 'Destination state', options: US_STATES, hint: 'Rates vary by state' },
};

// The countries endpoint does not return ISO codes - it returns the country
// name uppercased with underscores (UNITED_STATES_OF_AMERICA, NEW_ZEALAND) -
// while other call sites pass an ISO code or the display name. Match on all
// three, normalised, rather than assuming any one of them.
const ALIASES = {
  AU: ['AU', 'AUS', 'AUSTRALIA'],
  NZ: ['NZ', 'NZL', 'NEWZEALAND'],
  MY: ['MY', 'MYS', 'MALAYSIA'],
  CA: ['CA', 'CAN', 'CANADA'],
  US: ['US', 'USA', 'UNITEDSTATES', 'UNITEDSTATESOFAMERICA'],
};

const KEY_BY_ALIAS = Object.entries(ALIASES).reduce((acc, [key, names]) => {
  names.forEach((n) => { acc[n] = key; });
  return acc;
}, {});

// The field to show for a destination, or null when the country is priced at
// country level and asking would be a pointless extra step.
export const zoneFieldFor = (country) => {
  const token = String(country || '').toUpperCase().replace(/[^A-Z]/g, '');
  return ZONES[KEY_BY_ALIAS[token]] || null;
};

export default ZONES;
